import React, { useEffect, useState, memo } from "react";
import {
  FormGroup,
  Label,
  Input,
  Button,
  Spinner,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import {
  useDetailQuery,
  useCreateMutation,
  useUpdateMutation,
} from "helpers/http-client/hooks/useCrud";
import { API_ENDPOINTS } from "helpers/http-client/httpConstants";

interface SideEffectFormProps {
  sideEffectId?: string;
  onSuccess: (sideEffect: any) => void;
}

export interface SideEffectFormValues {
  name: string;
  description?: string;
  severity?: "Mild" | "Moderate" | "Severe";
  frequency?: string;
}

const SideEffectForm: React.FC<SideEffectFormProps> = ({ sideEffectId, onSuccess }) => {
  const { t } = useTranslation();
  const isEdit = Boolean(sideEffectId);
  const url = isEdit ? API_ENDPOINTS.SIDE_EFFECTS : "";

  const { data, isLoading } = useDetailQuery<SideEffectFormValues>(url, sideEffectId);

  const [formValues, setFormValues] = useState<SideEffectFormValues>({
    name: "",
    description: "",
    severity: undefined,
    frequency: "",
  });

  const { mutate: createSideEffect } = useCreateMutation(API_ENDPOINTS.SIDE_EFFECTS);
  const { mutate: updateSideEffect } = useUpdateMutation(API_ENDPOINTS.SIDE_EFFECTS);

  useEffect(() => {
    if (data && isEdit) {
      setFormValues({
        name: data.name || "",
        description: data.description || "",
        severity: data.severity || undefined,
        frequency: data.frequency || "",
      });
    }
  }, [data, isEdit]);

  const handleChange = (field: keyof SideEffectFormValues, value: string) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = () => {
    const payload: SideEffectFormValues = {
      name: formValues.name.trim(),
      description: formValues.description?.trim(),
      severity: formValues.severity,
      frequency: formValues.frequency?.trim(),
    };

    if (isEdit && sideEffectId) {
      updateSideEffect({ id: sideEffectId, data: payload }, { onSuccess });
    } else {
      createSideEffect(payload, { onSuccess });
    }
  };

  if (isEdit && isLoading) {
    return <Spinner color="primary" />;
  }

  return (
    <>
      <FormGroup>
        <Label>
          {t("Side Effect Name")}{" "}
          <span
            className="text-primary"
            style={{ cursor: "help" }}
            title="Enter the name of the side effect. Example: Headache, Nausea"
          >
            ?
          </span>
        </Label>
        <Input
          value={formValues.name}
          placeholder={t("e.g., Headache")}
          onChange={(e) => handleChange("name", e.target.value)}
        />
      </FormGroup>

      <FormGroup>
        <Label>
          {t("Description")}{" "}
          <span
            className="text-primary"
            style={{ cursor: "help" }}
            title="Provide a short description of the side effect if necessary."
          >
            ?
          </span>
        </Label>
        <Input
          type="textarea"
          value={formValues.description}
          placeholder={t("Optional detailed description")}
          onChange={(e) => handleChange("description", e.target.value)}
        />
      </FormGroup>

      <FormGroup>
        <Label>
          {t("Severity")}{" "}
          <span
            className="text-primary"
            style={{ cursor: "help" }}
            title="Classify the severity of this side effect."
          >
            ?
          </span>
        </Label>
        <Input
          type="select"
          value={formValues.severity || ""}
          onChange={(e) => handleChange("severity", e.target.value)}
        >
          <option value="">{t("Select severity")}</option>
          <option value="Mild">{t("Mild")}</option>
          <option value="Moderate">{t("Moderate")}</option>
          <option value="Severe">{t("Severe")}</option>
        </Input>
      </FormGroup>

      <FormGroup>
        <Label>
          {t("Frequency")}{" "}
          <span
            className="text-primary"
            style={{ cursor: "help" }}
            title="How often does this side effect occur? Example: Rare, Common, Frequently"
          >
            ?
          </span>
        </Label>
        <Input
          value={formValues.frequency}
          placeholder={t("e.g., Common")}
          onChange={(e) => handleChange("frequency", e.target.value)}
        />
      </FormGroup>

      <div className="d-flex justify-content-end">
        <Button color="primary" onClick={handleSubmit}>
          {isEdit ? t("Update") : t("Save")}
        </Button>
      </div>
    </>
  );
};

export default memo(SideEffectForm);
